import { useSyncExternalStore } from "react";

/**
 * Light or dark, as a choice the user makes.
 *
 * Stored in `localStorage` rather than on `users`, because the theme is a property of the
 * *device* and not of the account: a technician on a shared workshop tablet in a bright bay
 * and the same technician on their own phone at night want different answers, and `locale`
 * — which is a property of the person — is already the server-side preference.
 *
 * The class on `<html>` is the single source of truth, which is what lets the inline boot
 * script in each `index.html` settle the theme before the bundle parses without this module
 * and that script having to agree on anything but the storage key.
 */
export type Theme = "light" | "dark";

export const THEMES = ["light", "dark"] as const satisfies readonly Theme[];

export const THEME_STORAGE_KEY = "colorglo.theme";

const listeners = new Set<() => void>();

function read(): Theme {
  return document.documentElement.classList.contains("light") ? "light" : "dark";
}

export function setTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle("light", theme === "light");
  root.classList.toggle("dark", theme === "dark");

  // Private browsing on iOS throws rather than returning null, and a theme that failed to
  // persist is still a theme that applied.
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}

  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, read, () => "light");
}
